import React from 'react';
import styled, { css } from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import idx from 'idx';
import { MaxWidth } from '../../base';
import LoadingLine from './LoadingLine';
import { useTranslation } from 'react-i18next';
import styles from '../../utils/styles/css/styles';
import Desktop from '../../utils/styles/css/MediaQuery/Desktop';
import TouchableItem from './TouchableItem';

const TableContainer = styled.div`
  background-color: ${({ theme: { colors } }) => colors.light.primary};
  border-radius: 4px;
  width: 100%;
`;

const Cell = styled.div`
  flex: 1;
  padding: 24px 15px;
  display: flex;
  word-break: break-word;
  align-items: center;
  overflow: hidden;

  ${MaxWidth.sm`
    padding: 8px;
  `}
`;

const Row = styled(TouchableItem)`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  border-bottom: 1px solid ${({ theme: { colors } }) => colors.light.neutral} !important;

  ${MaxWidth.sm`
    margin: 0 5px;
  `}

  ${MaxWidth.sm`
    padding: 12px 5px
    flex-wrap: wrap;
    ${Cell} {
      flex: 1 1 50% !important;
    }
  `}
`;

const Header = styled(Cell)`
  text-align: left;
  color: ${({ theme: { colors } }) => colors.gray.neutral};
  border-bottom: 1px solid ${({ theme: { colors } }) => colors.gray.light} !important;
  text-transform: uppercase;
  font-size: 12px;
`;

const HeaderRow = styled(Row)`
  margin: 0 0;
  > ${Cell} {
    padding: 24px 15px;
    ${MaxWidth.sm`
      :first-child {
        padding-left: 25px;
      }
      :last-child {
        padding-right: 25px;
      }
    `}
  }
`;

export default function Table({
  loading = false,
  headers = [],
  data = [],
  sections = [],
  tableFlexs = [],
  onRowClick = null,
  showEmptyState = true
}) {
  const { t } = useTranslation();
  return (
    <TableContainer>
      {!isEmpty(headers) && (
        <Desktop>
          <HeaderRow>
            {headers.map((header, index) => {
              const flex = tableFlexs[index] || 1;
              return (
                <Header key={index} style={{ flex }}>
                  <div
                    css={css`
                      width: 100%;
                    `}
                  >
                    {header}
                  </div>
                </Header>
              );
            })}
          </HeaderRow>
        </Desktop>
      )}
      {loading && <LoadingLine height={5} />}
      <div>
        {sections.map((section, index) => (
          <React.Fragment key={index}>
            <Row css={[styles.pam, styles.bggp, styles.fwb]}>{section.title}</Row>
            {section.data.map((row, rowIdx) => (
              <Row key={rowIdx} onClick={onRowClick ? () => onRowClick(row, rowIdx) : undefined}>
                {row.map((cell, index) => {
                  const Element = idx(cell, _ => cell.render) || (() => cell);
                  const flex = tableFlexs[index] || 1;
                  return (
                    <Cell key={index} style={{ flex }}>
                      <Element />
                    </Cell>
                  );
                })}
              </Row>
            ))}
          </React.Fragment>
        ))}
        {loading && isEmpty(data) && (
          <Row>
            <Cell
              css={css`
                justify-content: center;
              `}
            >
              {t('general.loading', 'Loading')}...
            </Cell>
          </Row>
        )}
        {!loading && isEmpty(data) && isEmpty(sections) && showEmptyState && (
          <Row>
            <Cell
              css={css`
                justify-content: center;
              `}
            >
              {t('general.no_data_to_show', 'No data to show')}.
            </Cell>
          </Row>
        )}
        {data.map((row, rowIdx) => (
          <Row key={rowIdx} onClick={onRowClick ? () => onRowClick(row, rowIdx) : undefined}>
            {row.map((cell, index) => {
              const Element = idx(cell, _ => cell.render) || (() => cell);
              const flex = tableFlexs[index] || 1;
              return (
                <Cell key={index} style={{ flex }}>
                  <Element />
                </Cell>
              );
            })}
          </Row>
        ))}
      </div>
    </TableContainer>
  );
}
